// Shared Excel export.
//
// SheetJS is loaded globally from the CDN in public/index.html (xlsx-0.20.1),
// so there's no import — it's `window.XLSX`.
//
// The reason this is shared rather than another local helper: the pages that
// export dates were writing them as display STRINGS ("30 Jul 2026"). In Excel
// those are just text that looks like a date — they sort alphabetically
// ("01 Aug" before "30 Jul"), the date filters (This Month / Last Year) never
// appear, and they're useless in formulas. Passing a real Date through
// `cellDates` makes them genuine date cells instead.

/**
 * Coerce a value into a Date for `exportSheet`, or null when there isn't one.
 *
 * Returning null matters: it writes a genuinely blank cell, whereas letting an
 * invalid date through produces 1900-01-00 in Excel, which reads as real data.
 *
 * Accepts what the backend actually sends — 'YYYY-MM-DD' from SQL's
 * CONVERT(varchar, col, 23), full ISO timestamps, and Date objects.
 */
export function toDate(value) {
    if (value === null || value === undefined || value === '' || value === '—') return null;

    // Every date column in these exports is a CALENDAR date — none carries a
    // meaningful time. Anything left on the clock turns the Excel serial into a
    // fraction (e.g. 46233.2291), and a non-whole-day serial silently breaks
    // Excel's date filters and any `=A2=DATE(2026,7,30)` comparison.
    if (value instanceof Date) {
        if (isNaN(value.getTime())) return null;
        return new Date(value.getFullYear(), value.getMonth(), value.getDate());
    }

    const str = String(value).trim();
    if (!str) return null;

    // Date-only strings are parsed by hand rather than by `new Date(str)`, which
    // reads 'YYYY-MM-DD' as UTC midnight — that lands on the previous day for
    // anyone behind UTC and would shift every exported date by one.
    const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
    if (ymd) {
        const d = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
        return isNaN(d.getTime()) ? null : d;
    }

    // A timestamp: SQL DATE columns come back as UTC midnight
    // ('2026-07-30T00:00:00.000Z'), so the UTC calendar date is the intended
    // one — reading local components would shift it. Same convention
    // POTracker.jsx's own formatDate() already uses (getUTCDate() etc.).
    const t = new Date(str);
    if (isNaN(t.getTime())) return null;
    return new Date(t.getUTCFullYear(), t.getUTCMonth(), t.getUTCDate());
}

/**
 * Coerce a value into a Number for `exportSheet`, or null when there isn't one.
 *
 * Quantities and money must land as real numbers — a column with even one text
 * cell in it can't be SUMmed, averaged or number-filtered, and Excel gives no
 * warning that it's happening. The ₹/comma stripping is defensive: it catches a
 * display-formatted string ("₹33,942") that slips through from a UI field
 * instead of writing it into the sheet as text.
 */
export function num(value) {
    if (value === null || value === undefined || value === '' || value === '—') return null;
    if (typeof value === 'number') return isFinite(value) ? value : null;

    const cleaned = String(value).replace(/[₹,\s]/g, '');
    if (!cleaned) return null;
    const n = Number(cleaned);
    return isFinite(n) ? n : null;
}

/**
 * Write `rows` to an .xlsx file and trigger the download.
 *
 * Any cell whose value is a `Date` becomes a real Excel date cell; everything
 * else is written as-is. So a column is marked as a date simply by passing
 * `toDate(...)` for it — there is no list of date column indices to keep in
 * sync, which is how this kind of helper usually ends up formatting the wrong
 * column after someone reorders the headers.
 */
export function exportSheet({ headers, rows, sheetName = 'Data', filename }) {
    const XLSX = window.XLSX;
    if (!XLSX) { alert('SheetJS not loaded'); return false; }

    // null/'' become genuinely empty cells rather than t:'s' empty-string cells.
    // An empty *string* still counts as text, which is enough to turn an
    // otherwise-numeric column into a mixed-type one that Excel won't SUM.
    const body = rows.map((row) => row.map((v) => (v === null || v === undefined || v === '' ? null : v)));

    // cellDates keeps Date values as date cells (t:'d') instead of stringifying
    // them, which is the whole point of this helper.
    const ws = XLSX.utils.aoa_to_sheet([headers, ...body], { cellDates: true });

    // `z` must be a format STRING, not the numeric id of a built-in format.
    // Setting `z = 14` makes the writer mint a CUSTOM format whose pattern is
    // the two literal characters "1" and "4", and Excel then prints "14" in
    // every date cell. Passing the built-in's format string instead lets the
    // writer recognise it and reference built-in numFmtId 14 — which is what
    // makes Excel render it as its own locale's Short Date (30/07/2026 here,
    // 7/30/26 on a US machine) rather than a pattern pinned to one locale.
    const SHORT_DATE = XLSX.SSF.get_table()[14];
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let r = range.s.r; r <= range.e.r; r++) {
        for (let c = range.s.c; c <= range.e.c; c++) {
            const cell = ws[XLSX.utils.encode_cell({ r, c })];
            if (cell && cell.t === 'd') cell.z = SHORT_DATE;
        }
    }

    ws['!cols'] = headers.map((h) => ({ wch: Math.max(String(h).length + 2, 14) }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, filename);
    return true;
}

/** 'DD-MM-YYYY' for filenames, in local time (not UTC — see toDate). */
export function todayStamp() {
    const d = new Date();
    return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
}
